// LE PLAN DE DISSOLVE, sans GPU : l'automate qui choisit la désintégration de
// chaque mesure, la ligne de temps d'une mesure (dissolution sur les deux
// premiers temps, retour sur les deux suivants), le délai de libération de
// chaque particule selon le mode, et le retour par ressort amorti avec
// recalage exact — reproduit ici tel que le shader le calcule, pour le tester.

export const MODES = ['wind', 'explode', 'vortex', 'rain', 'pixels', 'sand'];
export const MODE_ID = { wind: 0, explode: 1, vortex: 2, rain: 3, pixels: 4, sand: 5 };

// la mesure : [0, 2) dissolution (chaque particule part à son délai), [2, 4) retour
export const DISSOLVE_BEATS = 2, RETURN_START = 2, SNAP_START = 3.6, SNAP_BEATS = 0.3;   // recalage fini à 3,9 : exact avec 0,1 temps d'avance
// le ressort du retour : critique, règle sur ~0,75 temps
export const OMEGA = 8.0;

// l'automate : jamais un mode des deux dernières mesures, les plus anciens
// favorisés ; l'énergie favorise l'explosion et le tourbillon. `history` : les
// derniers modes joués (le plus récent en fin de liste) - ou un seul mode
export function nextMode(history, rnd, energy) {
  const hist = Array.isArray(history) ? history : [history];
  const weights = MODES.map((m) => {
    const back = hist.length - 1 - hist.lastIndexOf(m);        // depuis combien de mesures
    if (hist.includes(m) && back < 2) return 0;
    let w = hist.includes(m) ? Math.min(1, 0.35 + 0.2 * back) : 1.2;
    if (m === 'explode' || m === 'vortex') w += 1.2 * (energy || 0);
    if (m === 'sand' || m === 'rain') w += 0.6 * (1 - (energy || 0));
    return w;
  });
  const total = weights.reduce((a, b) => a + b, 0);
  let x = rnd * total;
  for (let i = 0; i < MODES.length; i++) { x -= weights[i]; if (x <= 0) return MODES[i]; }
  return MODES[MODES.length - 1];
}

// le délai de libération (en temps, dans [0, 1,4]) d'une particule d'origine
// (ox, oy) (image dans [-w, w] x [-0,7, 0,7]) et de hasard h ; comme le shader
export function releaseDelay(mode, ox, oy, h, w) {
  const W = w || 0.7;
  const r = Math.hypot(ox / W, oy / 0.7);
  switch (mode) {
    case 'wind': return Math.max(0, Math.min(1.4, (ox + W) / (2 * W) * 1.2 + (h - 0.5) * 0.25));
    case 'explode': return Math.min(0.5, r * 0.45);
    case 'vortex': return Math.min(1.0, r * 0.7 + h * 0.15);
    case 'rain': return Math.min(1.4, h * 1.0 + (0.7 - oy) / 1.4 * 0.4);
    case 'pixels': return Math.min(1.2, h * 1.2);
    case 'sand': return Math.max(0, Math.min(1.4, (0.7 - oy) / 1.4 * 1.3 + (h - 0.5) * 0.2));
  }
  return 0;
}

// le retour : à partir du temps t (en temps de la mesure), position p, vitesse v,
// origine o ; le ressort critique part à RETURN_START + délai (distance), puis le
// recalage exact sur les SNAP_BEATS derniers temps. Un pas d'intégration ; renvoie [p, v].
export function returnStep(t, dtBeats, p, v, o, delay) {
  const active = t >= RETURN_START + delay;
  let np = p, nv = v;
  if (active) {
    const a = -OMEGA * OMEGA * (p - o) - 2 * OMEGA * v;
    nv = v + a * dtBeats;
    np = p + nv * dtBeats;
  }
  // le recalage : la fin de la mesure arrive exactement sur l'origine
  const snap = (t - SNAP_START) / SNAP_BEATS;
  if (snap > 0) { const k = Math.min(1, snap); np = np + (o - np) * k; nv = nv * (1 - k); }
  if (Math.abs(np - o) < 0.0008 && Math.abs(nv) < 0.02) { np = o; nv = 0; }    // (comme le shader : posée, exactement)
  return [np, nv];
}
// le délai du retour : lié à la distance (plus loin, plus tard) : la vague
export function returnDelay(dist) { return Math.min(0.7, dist * 0.4); }
